import { feedbackInputSchema, feedbackKinds, FEEDBACK_BODY_MAX, type FeedbackInput, type FeedbackKind } from '@koderek/shared';
import { ActionIcon, Affix, Alert, Button, Group, Modal, SegmentedControl, Stack, Text, Textarea } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { matchPath, useLocation } from 'react-router';
import { api } from '../../api/client';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconCheck, IconMessage } from '../../components/icons';
import { t } from '../../texts/pl';
import classes from './FeedbackButton.module.css';

const f = t.feedback;

/** Ekrany z adresem edycji (po `slug`) – opinia trafia do tej edycji. */
const EDITION_PATHS = [
  '/edycje/:slug/*',
  '/obsluga/konta/:id/edycje/:slug/*',
  '/obsluga/zajecia/:slug/*',
  '/obsluga/odprawa/:slug/*',
  '/statystyki/:slug',
];

export function editionSlugFromPath(pathname: string): string | undefined {
  for (const pattern of EDITION_PATHS) {
    const slug = matchPath(pattern, pathname)?.params.slug;
    if (slug) return slug;
  }
  return undefined;
}

/**
 * Ekrany bez przycisku: odprawa (skaner i okno rodziny mają akcje na dole ekranu) i – na telefonie – lista obecności
 * zajęć. Wydruk chowa przycisk sam (CSS).
 */
function hiddenOn(pathname: string, wide: boolean): boolean {
  if (pathname.startsWith('/odprawa/') || pathname.startsWith('/obsluga/odprawa')) return true;
  return !wide && !!matchPath('/obsluga/zajecia/:slug/:activityId', pathname);
}

/**
 * US-708: pływający przycisk „Twoja opinia” na każdym ekranie dla zalogowanych (PRD §10 pkt 53; gość go nie widzi –
 * decyduje `Layout`). Na telefonie i w panelu admina sama ikona, żeby nie zasłaniała agendy ani tabel.
 */
export function FeedbackButton() {
  const [opened, { open, close }] = useDisclosure(false);
  const { pathname } = useLocation();
  const wide = useMediaQuery('(min-width: 48em)', true);
  if (hiddenOn(pathname, wide) && !opened) return null;
  const compact = !wide || pathname.startsWith('/admin');
  return (
    <>
      <Affix position={{ bottom: 20, right: 20 }} zIndex={150} className={classes.affix}>
        {compact ? (
          <ActionIcon onClick={open} size={52} radius="xl" color="grape" aria-label={f.button} title={f.button} style={{ boxShadow: 'var(--mantine-shadow-md)' }}>
            <IconMessage size={26} />
          </ActionIcon>
        ) : (
          <Button onClick={open} leftSection={<IconMessage size={18} />} radius="xl" size="md" variant="filled" color="grape" style={{ boxShadow: 'var(--mantine-shadow-md)' }}>
            {f.button}
          </Button>
        )}
      </Affix>
      <Modal opened={opened} onClose={close} title={<Text fw={700}>{f.title}</Text>} size="lg" radius="lg">
        {/* Formularz montowany przy każdym otwarciu – po wysłaniu następne otwarcie zaczyna od pustego. */}
        {opened ? <FeedbackForm onClose={close} /> : null}
      </Modal>
    </>
  );
}

/**
 * Każdy rodzaj ma własne pole – po przełączeniu zakładki tekst zostaje, a „Wyślij” wysyła wszystkie wypełnione naraz
 * (osobna opinia na rodzaj – w panelu admina filtr po rodzaju działa bez zmian). Przy błędzie wysłane pola są czyszczone,
 * reszta zostaje do ponownej próby.
 */
export function FeedbackForm({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const [kind, setKind] = useState<FeedbackKind>('problem');
  const [bodies, setBodies] = useState<Record<FeedbackKind, string>>({ positive: '', problem: '', improvement: '' });
  const [invalid, setInvalid] = useState<{ kind: FeedbackKind; message: string } | null>(null);
  const horizontal = useMediaQuery('(min-width: 36em)', true);
  const filled = feedbackKinds.filter((k) => bodies[k].trim() !== '');

  const send = useMutation({
    mutationFn: async (items: FeedbackInput[]) => {
      for (const item of items) {
        await api('POST', '/feedback', { body: item });
        setBodies((prev) => ({ ...prev, [item.kind]: '' }));
      }
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!filled.length) {
      setInvalid({ kind, message: f.emptyAll });
      return;
    }
    const items: FeedbackInput[] = [];
    for (const k of filled) {
      const parsed = feedbackInputSchema.safeParse({
        kind: k,
        body: bodies[k],
        pagePath: location.pathname,
        editionSlug: editionSlugFromPath(location.pathname),
      });
      if (!parsed.success) {
        setKind(k);
        setInvalid({ kind: k, message: parsed.error.issues[0]?.message ?? f.emptyAll });
        return;
      }
      items.push(parsed.data);
    }
    setInvalid(null);
    send.mutate(items);
  };

  if (send.isSuccess) {
    return (
      <Stack gap="md">
        <Alert color="green">{f.thanks}</Alert>
        <Group justify="flex-end">
          <Button onClick={onClose}>{f.close}</Button>
        </Group>
      </Stack>
    );
  }

  const others = filled.filter((k) => k !== kind);
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {f.intro}
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={500} id="feedback-kind-label">
            {f.kindLabel}
          </Text>
          <SegmentedControl
            aria-labelledby="feedback-kind-label"
            fullWidth
            color="grape"
            withItemsBorders={false}
            orientation={horizontal ? 'horizontal' : 'vertical'}
            value={kind}
            onChange={(v) => setKind(v as FeedbackKind)}
            data={feedbackKinds.map((k) => ({
              value: k,
              label: (
                <Group gap={4} justify="center" wrap="nowrap">
                  <span>{f.kinds[k]}</span>
                  {/* Wypełnione pole – widać, co pójdzie razem z bieżącym. */}
                  {filled.includes(k) ? <IconCheck size={14} role="img" aria-hidden={false} aria-label={f.filled} /> : null}
                </Group>
              ),
            }))}
          />
        </Stack>
        <Textarea
          key={kind}
          label={f.bodyLabel}
          placeholder={f.placeholders[kind]}
          autosize
          minRows={4}
          maxRows={10}
          maxLength={FEEDBACK_BODY_MAX}
          data-autofocus
          description={`${bodies[kind].length} / ${FEEDBACK_BODY_MAX}`}
          value={bodies[kind]}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setBodies((prev) => ({ ...prev, [kind]: value }));
            if (invalid?.kind === kind) setInvalid(null);
          }}
          error={invalid?.kind === kind ? invalid.message : undefined}
        />
        {others.length ? (
          <Text size="sm" c="dimmed">
            {f.alsoSending(others.map((k) => f.kinds[k]).join(', '))}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            {f.multiHint}
          </Text>
        )}
        {send.isError ? <ErrorAlert error={send.error} /> : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {f.cancel}
          </Button>
          <Button type="submit" loading={send.isPending}>
            {filled.length > 1 ? f.sendMany(filled.length) : f.send}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
